import { useState, useCallback, useContext } from 'react';
import UserContext from '../../../contexts/UserContext';
import { ADVANCED_PLANS } from '../../../utils/PlanUtils';

const PRO_PLANS = ADVANCED_PLANS.map(Number);

/**
 * PRO node keys blocked in the canvas sidebar.
 */
export const PRO_SIDEBAR_KEYS = ['api_call', 'send_email', 'set_variable', 'email_monitor', 'sheets_reader'];

/**
 * PRO integration keys blocked in IntegrationsTab.
 */
export const PRO_INTEGRATION_KEYS = ['rdstation', 'zapier'];

/**
 * Hook for PRO feature gating.
 *
 * Returns:
 * - isProPlan: boolean indicating if user has plan 2 or 5
 * - upgradeModal: state object { show, featureName, featureDescription, featureIcon, featureGradient }
 * - openUpgradeModal(opts): open the modal with feature info
 * - closeUpgradeModal(): close the modal
 * - requirePro(opts): if not PRO opens modal and returns false; if PRO returns true
 */
const useProFeature = () => {
  const { getUserPlan, isSubaccount, getOrganizationInfo } = useContext(UserContext);

  // Subaccount herda o plano da organização-mãe. Sem isso, toda subaccount
  // (que tem plan_id=1 fixo) seria tratada como não-PRO, mesmo quando a
  // organização-mãe tem plano avançado (ex: parent_organization.plan_id=2).
  const effectivePlan = (() => {
    if (isSubaccount && isSubaccount()) {
      const org = getOrganizationInfo && getOrganizationInfo();
      if (org?.plan_id) return org.plan_id;
    }
    return getUserPlan();
  })();

  const isProPlan = PRO_PLANS.includes(Number(effectivePlan));

  const [upgradeModal, setUpgradeModal] = useState({
    show: false,
    featureName: '',
    featureDescription: '',
    featureIcon: null,
    featureGradient: '',
  });

  const openUpgradeModal = useCallback(({ featureName, featureDescription, featureIcon, featureGradient } = {}) => {
    setUpgradeModal({
      show: true,
      featureName: featureName || 'Recurso Premium',
      featureDescription: featureDescription || 'Este recurso está disponível no plano Business.',
      featureIcon: featureIcon || null,
      featureGradient: featureGradient || '',
    });
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setUpgradeModal((prev) => ({ ...prev, show: false }));
  }, []);

  const requirePro = useCallback(
    (opts = {}) => {
      if (isProPlan) return true;
      openUpgradeModal(opts);
      return false;
    },
    [isProPlan, openUpgradeModal]
  );

  return {
    isProPlan,
    upgradeModal,
    openUpgradeModal,
    closeUpgradeModal,
    requirePro,
  };
};

export default useProFeature;
